import { createSlice } from "@reduxjs/toolkit";
import {
  checkExtractionHealth,
  processDocument,
  fetchDocuments,
  fetchDocumentById,
  fetchStatistics,
  deleteDocument,
  searchDocuments,
  fetchMyDocuments,
  fetchAllRecentDocuments,
  fetchDocumentsByType,
  updateDocumentParameters,
  fetchDocumentHistory,
  fetchUserStorage,
  recalculateUserStorage,
   shareDocument,
  shareDocuments,
  fetchDocumentAccessList,
  revokeDocumentAccess,

} from "../thunks/extractionThunks";

const initialState = {
  // Health check
  healthStatus: null,
  healthLoading: false,
  healthError: null,

  // Process document
  processResult: null,
  processLoading: false,
  processError: null,

  // Documents by collection
  documents: [],
  documentsLoading: false,
  documentsError: null,
  documentsCount: 0,
  currentCollection: null,

  // My documents (all user documents)
  myDocuments: [],
  myDocumentsLoading: false,
  myDocumentsError: null,
  myDocumentsTotal: 0,

  // ✅ Documents by type — state séparé pour éviter le flash
  documentsByType: [],
  documentsByTypeLoading: false,
  documentsByTypeError: null,
  documentsByTypeTotal: 0,

  // Current document
  currentDocument: null,
  documentLoading: false,
  documentError: null,

  // Statistics
  statistics: null,
  statisticsLoading: false,
  statisticsError: null,

  // Delete
  deleteLoading: false,
  deleteError: null,
  deleteSuccess: false,

  // Search
  searchResults: [],
  searchLoading: false,
  searchError: null,

  // Update parameters
  updateLoading: false,
  updateError: null,
  updateSuccess: false,

  // Document history
  documentHistory: null,
  historyLoading: false,
  historyError: null,

  // Storage
  userStorage: null,
  storageLoading: false,
  storageError: null,

  // Sharing
shareLoading: false,
shareError: null,
shareSuccess: false,
shareResult: null,

// Batch sharing
batchShareLoading: false,
batchShareError: null,
batchShareResult: null,

// Access list
accessList: [],
accessListLoading: false,
accessListError: null,

// Revoke access
revokeLoading: false,
revokeError: null,
revokeSuccess: false,
};

const extractionSlice = createSlice({
  name: "extraction",
  initialState,
  reducers: {
    resetProcessResult: (state) => {
      state.processResult = null;
      state.processError = null;
    },
    resetDocuments: (state) => {
      state.documents = [];
      state.documentsError = null;
      state.documentsCount = 0;
    },
    resetMyDocuments: (state) => {
      state.myDocuments = [];
      state.myDocumentsError = null;
      state.myDocumentsTotal = 0;
    },
    // ✅ Reset séparé pour documentsByType
    resetDocumentsByType: (state) => {
      state.documentsByType = [];
      state.documentsByTypeError = null;
      state.documentsByTypeTotal = 0;
    },
    resetCurrentDocument: (state) => {
      state.currentDocument = null;
      state.documentError = null;
    },
    resetSearchResults: (state) => {
      state.searchResults = [];
      state.searchError = null;
    },
    resetUpdateState: (state) => {
      state.updateLoading = false;
      state.updateError = null;
      state.updateSuccess = false;
    },
    resetDocumentHistory: (state) => {
      state.documentHistory = null;
      state.historyError = null;
    },
    resetDeleteState: (state) => {
      state.deleteLoading = false;
      state.deleteError = null;
      state.deleteSuccess = false;
    },
    resetStorageState: (state) => {
      state.userStorage = null;
      state.storageError = null;
    },
    resetShareState: (state) => {
  state.shareLoading = false;
  state.shareError = null;
  state.shareSuccess = false;
  state.shareResult = null;
},
resetBatchShareState: (state) => {
  state.batchShareLoading = false;
  state.batchShareError = null;
  state.batchShareResult = null;
},
resetAccessList: (state) => {
  state.accessList = [];
  state.accessListError = null;
},
resetRevokeState: (state) => {
  state.revokeLoading = false;
  state.revokeError = null;
  state.revokeSuccess = false;
},
  },


  extraReducers: (builder) => {
    // ==================== HEALTH CHECK ====================
    builder
      .addCase(checkExtractionHealth.pending, (state) => {
        state.healthLoading = true;
        state.healthError = null;
      })
      .addCase(checkExtractionHealth.fulfilled, (state, action) => {
        state.healthLoading = false;
        state.healthStatus = action.payload;
      })
      .addCase(checkExtractionHealth.rejected, (state, action) => {
        state.healthLoading = false;
        state.healthError = action.payload;
      });

    // ==================== PROCESS DOCUMENT ====================
    builder
      .addCase(processDocument.pending, (state) => {
        state.processLoading = true;
        state.processError = null;
        state.processResult = null;
      })
      .addCase(processDocument.fulfilled, (state, action) => {
        state.processLoading = false;
        state.processResult = action.payload;
        if (action.payload?.storage) {
          state.userStorage = action.payload.storage;
        }
      })
      .addCase(processDocument.rejected, (state, action) => {
        state.processLoading = false;
        state.processError = action.payload;
      });

    // ==================== FETCH DOCUMENTS BY COLLECTION ====================
    builder
      .addCase(fetchDocuments.pending, (state) => {
        state.documentsLoading = true;
        state.documentsError = null;
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.documentsLoading = false;
        state.documents = action.payload.documents || [];
        state.documentsCount = action.payload.count || 0;
        state.currentCollection = action.payload.collection;
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.documentsLoading = false;
        state.documentsError = action.payload;
      });

    // ==================== FETCH MY DOCUMENTS ====================
    builder
      .addCase(fetchMyDocuments.pending, (state) => {
        state.myDocumentsLoading = true;
        state.myDocumentsError = null;
      })
      .addCase(fetchMyDocuments.fulfilled, (state, action) => {
        state.myDocumentsLoading = false;
        state.myDocuments = action.payload.documents || [];
        state.myDocumentsTotal = action.payload.total || 0;
      })
      .addCase(fetchMyDocuments.rejected, (state, action) => {
        state.myDocumentsLoading = false;
        state.myDocumentsError = action.payload;
      });

    // ==================== FETCH ALL RECENT DOCUMENTS ====================
    builder
      .addCase(fetchAllRecentDocuments.pending, (state) => {
        state.myDocumentsLoading = true;
        state.myDocumentsError = null;
        state.myDocuments = [];
        state.myDocumentsTotal = 0;
      })
      .addCase(fetchAllRecentDocuments.fulfilled, (state, action) => {
        state.myDocumentsLoading = false;
        state.myDocuments = action.payload.documents || [];
        state.myDocumentsTotal = action.payload.total || 0;
      })
      .addCase(fetchAllRecentDocuments.rejected, (state, action) => {
        state.myDocumentsLoading = false;
        state.myDocumentsError = action.payload;
      });

    // ==================== FETCH DOCUMENTS BY TYPE ====================
    // ✅ Écrit dans documentsByType — plus dans myDocuments
    builder
      .addCase(fetchDocumentsByType.pending, (state) => {
        state.documentsByTypeLoading = true;
        state.documentsByTypeError = null;
      })
      .addCase(fetchDocumentsByType.fulfilled, (state, action) => {
        state.documentsByTypeLoading = false;
        state.documentsByType = action.payload.documents || [];
        state.documentsByTypeTotal = action.payload.total || 0;
      })
      .addCase(fetchDocumentsByType.rejected, (state, action) => {
        state.documentsByTypeLoading = false;
        state.documentsByTypeError = action.payload;
      });

    // ==================== FETCH DOCUMENT BY ID ====================
    builder
      .addCase(fetchDocumentById.pending, (state) => {
        state.documentLoading = true;
        state.documentError = null;
      })
      .addCase(fetchDocumentById.fulfilled, (state, action) => {
        state.documentLoading = false;
        state.currentDocument = action.payload;
      })
      .addCase(fetchDocumentById.rejected, (state, action) => {
        state.documentLoading = false;
        state.documentError = action.payload;
      });

    // ==================== DELETE DOCUMENT ====================
    builder
      .addCase(deleteDocument.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;
        if (action.payload?.storage) {
          state.userStorage = action.payload.storage;
        }
        if (action.meta.arg) {
          state.documents = state.documents.filter(
            (doc) => doc._id !== action.meta.arg.id
          );
          state.myDocuments = state.myDocuments.filter(
            (doc) => doc._id !== action.meta.arg.id
          );
          // ✅ Supprimer aussi dans documentsByType
          state.documentsByType = state.documentsByType.filter(
            (doc) => doc._id !== action.meta.arg.id
          );
        }
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
        state.deleteSuccess = false;
      });

    // ==================== UPDATE DOCUMENT PARAMETERS ====================
    builder
      .addCase(updateDocumentParameters.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateDocumentParameters.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;
        const updatedDocument = action.payload?.data;
        if (updatedDocument && state.currentDocument) {
          state.currentDocument = {
            ...state.currentDocument,
            ...updatedDocument,
          };
        }
        if (updatedDocument) {
          const updatedId = updatedDocument._id || updatedDocument.id;
          const replaceUpdatedDocument = (documents) =>
            documents.map((document) =>
              (document._id || document.id) === updatedId
                ? { ...document, ...updatedDocument }
                : document
            );
          state.documents = replaceUpdatedDocument(state.documents);
          state.myDocuments = replaceUpdatedDocument(state.myDocuments);
          state.documentsByType = replaceUpdatedDocument(state.documentsByType);
        }
      })
      .addCase(updateDocumentParameters.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
        state.updateSuccess = false;
      });

    // ==================== FETCH DOCUMENT HISTORY ====================
    builder
      .addCase(fetchDocumentHistory.pending, (state) => {
        state.historyLoading = true;
        state.historyError = null;
      })
      .addCase(fetchDocumentHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.documentHistory = action.payload;
      })
      .addCase(fetchDocumentHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.historyError = action.payload;
      });

    // ==================== SEARCH DOCUMENTS ====================
    builder
      .addCase(searchDocuments.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchDocuments.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload.results || [];
        state.searchCount = action.payload.count || 0;
      })
      .addCase(searchDocuments.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload;
      });

    // ==================== FETCH STATISTICS ====================
    builder
      .addCase(fetchStatistics.pending, (state) => {
        state.statisticsLoading = true;
        state.statisticsError = null;
      })
      .addCase(fetchStatistics.fulfilled, (state, action) => {
        state.statisticsLoading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchStatistics.rejected, (state, action) => {
        state.statisticsLoading = false;
        state.statisticsError = action.payload;
      });

    // ==================== FETCH USER STORAGE ====================
    builder
      .addCase(fetchUserStorage.pending, (state) => {
        state.storageLoading = true;
        state.storageError = null;
      })
      .addCase(fetchUserStorage.fulfilled, (state, action) => {
        state.storageLoading = false;
        state.userStorage = action.payload;
      })
      .addCase(fetchUserStorage.rejected, (state, action) => {
        state.storageLoading = false;
        state.storageError = action.payload;
      });

    // ==================== RECALCULATE USER STORAGE ====================
    builder
      .addCase(recalculateUserStorage.pending, (state) => {
        state.storageLoading = true;
        state.storageError = null;
      })
      .addCase(recalculateUserStorage.fulfilled, (state, action) => {
        state.storageLoading = false;
        state.userStorage = action.payload;
      })
      .addCase(recalculateUserStorage.rejected, (state, action) => {
        state.storageLoading = false;
        state.storageError = action.payload;
      });

      // ==================== SHARE DOCUMENT ====================
builder
  .addCase(shareDocument.pending, (state) => {
    state.shareLoading = true;
    state.shareError = null;
    state.shareSuccess = false;
    state.shareResult = null;
  })
  .addCase(shareDocument.fulfilled, (state, action) => {
    state.shareLoading = false;
    state.shareSuccess = true;
    state.shareResult = action.payload;
  })
  .addCase(shareDocument.rejected, (state, action) => {
    state.shareLoading = false;
    state.shareError = action.payload;
    state.shareSuccess = false;
  });

// ==================== SHARE MULTIPLE DOCUMENTS ====================
builder
  .addCase(shareDocuments.pending, (state) => {
    state.batchShareLoading = true;
    state.batchShareError = null;
    state.batchShareResult = null;
  })
  .addCase(shareDocuments.fulfilled, (state, action) => {
    state.batchShareLoading = false;
    state.batchShareResult = action.payload;
  })
  .addCase(shareDocuments.rejected, (state, action) => {
    state.batchShareLoading = false;
    state.batchShareError = action.payload;
  });

// ==================== FETCH DOCUMENT ACCESS LIST ====================
builder
  .addCase(fetchDocumentAccessList.pending, (state) => {
    state.accessListLoading = true;
    state.accessListError = null;
  })
  .addCase(fetchDocumentAccessList.fulfilled, (state, action) => {
    state.accessListLoading = false;
    state.accessList = action.payload.accessList || [];
  })
  .addCase(fetchDocumentAccessList.rejected, (state, action) => {
    state.accessListLoading = false;
    state.accessListError = action.payload;
  });

// ==================== REVOKE DOCUMENT ACCESS ====================
builder
  .addCase(revokeDocumentAccess.pending, (state) => {
    state.revokeLoading = true;
    state.revokeError = null;
    state.revokeSuccess = false;
  })
  .addCase(revokeDocumentAccess.fulfilled, (state, action) => {
    state.revokeLoading = false;
    state.revokeSuccess = true;
    // Supprimer les médecins révoqués de l'accessList locale
    const revoked = action.payload.revokedDoctorIds?.map(String) || [];
    state.accessList = state.accessList.filter(
      (entry) => !revoked.includes(String(entry.doctorId || entry.id))
    );
  })
  .addCase(revokeDocumentAccess.rejected, (state, action) => {
    state.revokeLoading = false;
    state.revokeError = action.payload;
    state.revokeSuccess = false;
  });
  },
});

export const {
  resetProcessResult,
  resetDocuments,
  resetMyDocuments,
  resetDocumentsByType,
  resetCurrentDocument,
  resetSearchResults,
  resetUpdateState,
  resetDocumentHistory,
  resetDeleteState,
  resetStorageState,
   resetShareState,
  resetBatchShareState,
  resetAccessList,
  resetRevokeState,
} = extractionSlice.actions;

export default extractionSlice.reducer;
